import React, { useMemo } from 'react';
import {
  Drawer,
  List,
  Badge,
  Typography,
  Button,
  Space,
  Tag,
  Tooltip,
  Empty,
  Avatar,
  Dropdown,
  MenuProps,
  Spin,
  Alert
} from 'antd';
import {
  BellOutlined,
  CheckOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SettingOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseOutlined,
  MoreOutlined,
  EyeOutlined,
  FileTextOutlined,
  DollarOutlined,
  ToolOutlined
} from '@ant-design/icons';
import { useTheme } from '../context/ThemeContext';
import { useNotifications, Notification } from '../context/NotificationContext';
import { useNavigate } from 'react-router-dom';

const { Text, Paragraph } = Typography;

interface NotificationPanelProps {
  visible: boolean;
  onClose: () => void;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({ visible, onClose }) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    isLoading,
    error,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    refetchNotifications
  } = useNotifications();

  const isDarkMode = theme.algorithm?.[0]?.toString().includes('dark');

  const themeTokens = useMemo(() => ({
    background: isDarkMode ? '#1f1f1f' : '#ffffff',
    borderColor: isDarkMode ? '#303030' : '#f0f0f0',
    textColor: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.85)',
    textColorSecondary: isDarkMode ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
    itemHoverBackground: isDarkMode ? '#262626' : '#f5f5f5',
    unreadBackground: isDarkMode ? '#262626' : '#f0f6ff',
  }), [isDarkMode]);

  const getNotificationIcon = (type: Notification['type']) => {
    const iconProps = { style: { fontSize: '16px' } };
    switch (type) {
      case 'success':
        return <CheckCircleOutlined {...iconProps} style={{ ...iconProps.style, color: '#52c41a' }} />;
      case 'warning':
        return <WarningOutlined {...iconProps} style={{ ...iconProps.style, color: '#faad14' }} />;
      case 'error':
        return <ExclamationCircleOutlined {...iconProps} style={{ ...iconProps.style, color: '#ff4d4f' }} />;
      default:
        return <InfoCircleOutlined {...iconProps} style={{ ...iconProps.style, color: '#1890ff' }} />;
    }
  };

  const getPriorityColor = (priority?: Notification['priority']) => {
    switch (priority) {
      case 'critical': return '#ff4d4f';
      case 'high': return '#fa8c16';
      case 'medium': return '#1890ff';
      case 'low': return '#52c41a';
      default: return '#d9d9d9';
    }
  };

  const getCategoryColor = (category?: Notification['category']) => {
    switch (category) {
      case 'security': return 'red';
      case 'system': return 'blue';
      case 'user': return 'green';
      case 'update': return 'orange';
      case 'approval': return 'purple';
      default: return 'default';
    }
  };

  const formatTimestamp = (timestamp: Date) => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) {
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else if (days < 7) {
      return `${days}d ago`;
    } else {
      return timestamp.toLocaleDateString();
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    
    if (notification.category === 'approval' && 
        notification.entityType === 'expense_code' && 
        notification.entityId) {
      navigate(`/codevault/expense-code-config?modal=edit&id=${notification.entityId}`);
      onClose();
      return;
    }
    
    if (notification.category === 'approval' &&
        notification.entityType === 'income_code' &&
        notification.entityId) {
      navigate(`/codevault/income-code-config?modal=edit&id=${notification.entityId}`);
      onClose();
      return;
    }

    if (notification.category === 'approval' &&
        notification.entityType === 'project_code' &&
        notification.entityId) {
      navigate(`/codevault/project-code-config?action=approve&id=${notification.entityId}`);
      onClose();
      return;
    }

    if (notification.entityType === 'project_code' && notification.entityId) {
      navigate(`/codevault/project-code-config?id=${notification.entityId}`);
      onClose();
      return;
    }

    if (notification.category === 'approval' &&
        notification.entityType === 'specific_project_code' &&
        notification.entityId) {
      navigate(`/codevault/project-code-config?action=approve&specificId=${notification.entityId}`);
      onClose();
      return;
    }

    if (notification.entityType === 'specific_project_code' && notification.entityId) {
      navigate(`/codevault/project-code-config?specificId=${notification.entityId}`);
      onClose();
      return;
    }

    if (notification.category === 'approval' &&
        notification.entityType === 'fixed_asset') {
      const targetUrl = notification.actionUrl || (notification.entityId ? `/assetmaster/approval/view/${notification.entityId}` : undefined);
      if (targetUrl) {
        navigate(targetUrl);
        onClose();
      }
      return;
    }
    
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      onClose();
    }
  };

  const getNotificationMenuItems = (notification: Notification): MenuProps['items'] => [
    ...(notification.read ? [
      {
        key: 'unread',
        label: 'Mark as unread',
        icon: <EyeOutlined />,
        onClick: (e: any) => {
          e?.domEvent?.stopPropagation();
          markAsUnread(notification.id);
        },
      }
    ] : [
      {
        key: 'read',
        label: 'Mark as read',
        icon: <CheckOutlined />,
        onClick: (e: any) => {
          e?.domEvent?.stopPropagation();
          markAsRead(notification.id);
        },
      }
    ]),
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: (e: any) => {
        e?.domEvent?.stopPropagation();
        deleteNotification(notification.id);
      },
    }
  ];

  const sortedNotifications = useMemo(() => {
    return [...notifications].sort((a, b) => {
      // Sort by read status first (unread first), then by timestamp (newest first)
      if (a.read !== b.read) {
        return a.read ? 1 : -1;
      }
      return b.timestamp.getTime() - a.timestamp.getTime();
    });
  }, [notifications]);

  const headerActions = (
    <Space>
      <Tooltip title="Refresh notifications">
        <Button
          type="text"
          icon={<ReloadOutlined />}
          onClick={refetchNotifications}
          loading={isLoading}
          size="small"
        />
      </Tooltip>
      <Tooltip title="Mark all as read">
        <Button
          type="text"
          icon={<CheckOutlined />}
          onClick={markAllAsRead}
          disabled={unreadCount === 0}
          size="small"
        />
      </Tooltip>
      <Tooltip title="Notification settings">
        <Button
          type="text"
          icon={<SettingOutlined />}
          size="small"
        />
      </Tooltip>
    </Space>
  );

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Space align="center">
            <BellOutlined />
            <span>Notifications</span>
            {unreadCount > 0 && (
              <Badge 
                count={unreadCount} 
                size="small" 
                style={{ backgroundColor: '#ff4d4f' }}
                className={unreadCount > 0 ? 'notification-badge-pulse' : ''}
              />
            )}
          </Space>
          {headerActions}
        </div>
      }
      placement="right"
      onClose={onClose}
      open={visible}
      width={420}
      className="notification-panel"
      styles={{
        body: { 
          padding: 0,
          background: themeTokens.background 
        },
        header: {
          borderBottom: `1px solid ${themeTokens.borderColor}`,
          background: themeTokens.background
        }
      }}
      closeIcon={<CloseOutlined />}
    >
      {error && (
        <Alert
          message="Error loading notifications"
          description={error}
          type="error"
          showIcon
          style={{ margin: '16px' }}
          action={
            <Button size="small" onClick={refetchNotifications}>
              Retry
            </Button>
          }
        />
      )}

      {isLoading && (
        <div style={{ textAlign: 'center', padding: '24px' }}>
          <Spin size="large" />
        </div>
      )}

      {!isLoading && !error && (
        <>
          {notifications.length > 0 && (
            <div style={{ padding: '16px', borderBottom: `1px solid ${themeTokens.borderColor}` }}>
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Text type="secondary">
                  {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
                  {unreadCount > 0 && ` • ${unreadCount} unread`}
                </Text>
                {notifications.length > 0 && (
                  <Button 
                    type="link" 
                    size="small" 
                    onClick={clearAllNotifications}
                    style={{ padding: 0, color: themeTokens.textColorSecondary }}
                  >
                    Clear all
                  </Button>
                )}
              </Space>
            </div>
          )}

          {notifications.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span style={{ color: themeTokens.textColorSecondary }}>
                  No notifications yet
                </span>
              }
              style={{ 
                marginTop: '60px',
                color: themeTokens.textColorSecondary 
              }}
            />
          ) : (
            <List
              itemLayout="vertical"
              dataSource={sortedNotifications}
              style={{ height: 'calc(100vh - 120px)', overflow: 'auto' }}
              renderItem={(notification) => (
                <List.Item
                  key={notification.id}
                  className={`notification-priority-${notification.priority || 'medium'}`}
                  style={{
                    padding: '16px',
                    borderBottom: `1px solid ${themeTokens.borderColor}`,
                    backgroundColor: notification.read ? 'transparent' : themeTokens.unreadBackground,
                    cursor: notification.actionUrl ? 'pointer' : 'default',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (notification.actionUrl) {
                      e.currentTarget.style.backgroundColor = themeTokens.itemHoverBackground;
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = notification.read ? 'transparent' : themeTokens.unreadBackground;
                  }}
                  onClick={() => handleNotificationClick(notification)}
                  actions={[
                    <Dropdown
                      menu={{ items: getNotificationMenuItems(notification) }}
                      trigger={['click']}
                      key="more"
                    >
                      <Button 
                        type="text" 
                        icon={<MoreOutlined />} 
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Dropdown>
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar 
                        icon={getNotificationIcon(notification.type)}
                        style={{ backgroundColor: 'transparent' }}
                      />
                    }
                    title={
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <Text 
                          strong={!notification.read}
                          style={{ 
                            color: themeTokens.textColor,
                            fontSize: '14px',
                            flex: 1
                          }}
                        >
                          {notification.title}
                        </Text>
                        {!notification.read && (
                          <div 
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: '#1890ff',
                              flexShrink: 0
                            }}
                          />
                        )}
                      </div>
                    }
                    description={
                      <div>
                        <Paragraph
                          style={{ 
                            margin: 0,
                            marginBottom: '8px',
                            color: themeTokens.textColorSecondary,
                            fontSize: '13px',
                            lineHeight: '1.4'
                          }}
                          ellipsis={{ rows: 2, expandable: false }}
                        >
                          {notification.message}
                        </Paragraph>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                          <Space size="small">
                            {notification.category && (
                              <Tag 
                                color={getCategoryColor(notification.category)} 
                                style={{ fontSize: '11px', padding: '0 6px', margin: 0 }}
                              >
                                {notification.category}
                              </Tag>
                            )}
                            {notification.priority && (
                              <Tag
                                style={{
                                  fontSize: '11px',
                                  padding: '0 6px',
                                  margin: 0,
                                  backgroundColor: getPriorityColor(notification.priority),
                                  color: '#fff',
                                  border: 'none'
                                }}
                              >
                                {notification.priority}
                              </Tag>
                            )}
                          </Space>
                          <Text 
                            type="secondary" 
                            style={{ fontSize: '11px', color: themeTokens.textColorSecondary }}
                          >
                            {formatTimestamp(notification.timestamp)}
                          </Text>
                        </div>
                        {(notification.actionText || notification.category === 'approval') && (
                          <Button
                            type="link"
                            size="small"
                            icon={notification.category === 'approval' ? 
                              (notification.entityType === 'expense_code' ? <DollarOutlined /> : 
                               notification.entityType === 'income_code' ? <DollarOutlined /> : 
                               notification.entityType === 'fixed_asset' ? <ToolOutlined /> : <FileTextOutlined />) : 
                              undefined
                            }
                            style={{
                              padding: '4px 0',
                              height: 'auto',
                              fontSize: '12px',
                              marginTop: '4px'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNotificationClick(notification);
                            }}
                          >
                            {notification.actionText || 
                             (notification.category === 'approval' ? 'Review & Approve' : 'View Details')} →
                          </Button>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </>
      )}
    </Drawer>
  );
};

export default React.memo(NotificationPanel);